import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Tab, Snackbar, Alert } from '@mui/material';
import { deletarTodasEntidadesCategoria, reabrirCategoriaTorneio, CategoriaTorneio, FaseCategoriaTorneio } from '../../../services/categoriaTorneioService';
import { InscricoesPorTorneioData } from '../../../services/torneioService';
import CategoryMatchesHeader from './components/CategoryMatchesHeader';
import GroupsPhaseTab from './components/GroupsPhaseTab';
import EliminationPhaseTab from './components/EliminationPhaseTab';
import GerarPartidasButton from './components/GerarPartidasButton';
import { temFaseDeGrupos } from '../../../utils/temFaseDeGrupos';
import { temFaseEliminatoria } from '../../../utils/temFaseEliminatoria';

interface CategoryMatchesTabProps {
  categoriaId: string;
  torneioId: string;
  categoria: CategoriaTorneio;
  inscricoes: InscricoesPorTorneioData['inscricoesTorneioPorAtletaIdTorneioId'];
  canManage: boolean;
  isAdmin?: boolean;
  onRefresh?: () => void;
}

const CategoryMatchesTab: React.FC<CategoryMatchesTabProps> = ({
  categoriaId,
  torneioId,
  categoria,
  inscricoes,
  canManage,
  isAdmin = false,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const hasMatches = categoria?.partidas && categoria.partidas.length > 0;

  // Sem nenhuma fase nem partida criada: oferece o atalho "Gerar Partidas"
  // (mesmo comportamento do botão da aba "Sobre").
  const hasFases = (categoria?.fasesCategoria?.length ?? 0) > 0;
  const semEstrutura = !hasFases && !hasMatches;

  const inscricoesPagas = inscricoes.filter(
    (i) =>
      i.categoriaTorneio?.id === categoriaId &&
      i.status === 'CONFIRMADA',
  ).length;

  const handleResetMatches = async () => {
    if (!categoriaId) return;
    
    try {
      await deletarTodasEntidadesCategoria(categoriaId);
      setResetDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Partidas resetadas com sucesso',
        severity: 'success',
      });
      onRefresh?.();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Erro ao resetar partidas';
      setResetDialogOpen(false);
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    }
  };

  const handleReabrir = async () => {
    if (!categoriaId) return;
    try {
      await reabrirCategoriaTorneio(categoriaId);
      setSnackbar({
        open: true,
        message: 'Categoria reaberta para edição',
        severity: 'success',
      });
      onRefresh?.();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Erro ao reabrir categoria';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const faseGrupo = categoria?.fasesCategoria.find(
    (f) => f.tipoGrupoChave === 'GRUPO',
  ) || null;

  const fasesChave = categoria?.fasesCategoria.filter(
    (f) => f.tipoGrupoChave === 'CHAVE',
  ) || [];

  const showGroupsTab = !categoria?.modeloDisputa || temFaseDeGrupos(categoria.modeloDisputa);
  // Grupo Único é round-robin sem mata-mata — a aba "Eliminatórias" só aparece
  // para modelos com fase eliminatória (ou quando o modelo ainda é desconhecido).
  const showEliminationTab =
    !categoria?.modeloDisputa || temFaseEliminatoria(categoria.modeloDisputa);

  const abas: Array<'grupos' | 'eliminatorias'> = [
    ...(showGroupsTab ? (['grupos'] as const) : []),
    ...(showEliminationTab ? (['eliminatorias'] as const) : []),
  ];
  const abaAtiva = abas[tabValue] ?? abas[0];

  // Funções wrapper para adaptar diferentes interfaces de callbacks dos componentes filhos
  // para o mesmo callback onRefresh do componente pai
  const handleFaseCreated = () => {
    onRefresh?.();
  };

  const handleMatchUpdated = () => {
    onRefresh?.();
  };

  const handleUpdate = () => {
    onRefresh?.();
  };

  const handlePrint = () => {
    navigate(`/liga/tournament/${torneioId}/category/${categoriaId}/imprimir`);
  };

  return (
    <div className="mt-4">
      {/* Cabeçalho */}
      <CategoryMatchesHeader categoria={categoria} torneioId={torneioId} />

      {/* Botões de Ação */}
      <div className="mb-3 d-flex gap-2">
        <button
          className="btn btn-primary"
          onClick={handlePrint}
        >
          Imprimir
        </button>
        {canManage && semEstrutura && (
          <GerarPartidasButton
            categoriaId={categoriaId}
            categoria={categoria}
            inscricoesPagas={inscricoesPagas}
            onRefresh={onRefresh}
            buttonClassName="btn btn-outline-success"
          />
        )}
        {canManage && hasMatches && (
          <button
            className="btn btn-danger"
            onClick={() => setResetDialogOpen(true)}
          >
            Resetar partidas
          </button>
        )}
        {canManage && categoria?.status === 'ENCERRADA' && (
          <button
            className="btn btn-outline-warning"
            onClick={handleReabrir}
            title="Reabrir a categoria para corrigir resultados"
          >
            Reabrir categoria
          </button>
        )}
      </div>

      {/* Sistema de Abas */}
      <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
        <div className="card-body p-4">
          <Tabs
            value={Math.min(tabValue, Math.max(0, abas.length - 1))}
            onChange={handleTabChange}
            aria-label="abas de partidas"
            indicatorColor="primary"
            textColor="primary"
          >
            {abas.map((aba) => (
              <Tab key={aba} label={aba === 'grupos' ? 'Fase de Grupos' : 'Eliminatórias'} />
            ))}
          </Tabs>

          {/* Conteúdo das Abas */}
          {abaAtiva === 'grupos' && (
            <GroupsPhaseTab
              categoriaId={categoriaId}
              faseGrupo={faseGrupo}
              onFaseCreated={handleFaseCreated}
              onMatchUpdated={handleMatchUpdated}
              canManage={canManage}
              isAdmin={isAdmin}
              modeloDisputa={categoria?.modeloDisputa}
            />
          )}

          {abaAtiva === 'eliminatorias' && (
            <EliminationPhaseTab
              categoriaId={categoriaId}
              fasesChave={fasesChave}
              onUpdate={handleUpdate}
              canManage={canManage}
              isAdmin={isAdmin}
            />
          )}
        </div>
      </div>

      {/* Dialog de Reset de Partidas */}
      {resetDialogOpen && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setResetDialogOpen(false)} />
          <div
            className="modal fade show d-block"
            role="dialog"
            tabIndex={-1}
            style={{ overflow: 'auto' }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirmar Reset de Partidas</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Fechar"
                    onClick={() => setResetDialogOpen(false)}
                  />
                </div>
                <div className="modal-body">
                  <p>
                    Tem certeza que deseja resetar todas as partidas desta categoria? Esta ação irá deletar todas as partidas e fases atuais. Esta ação não pode ser desfeita.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setResetDialogOpen(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleResetMatches}
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CategoryMatchesTab;

